import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CLAIM_TYPES = [["defective", "Defective"], ["incompatible", "Incompatible"], ["missing", "Missing"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

/** Claims → "New claim": type an order number → CRM order(s) or the Shopify order (imported on demand) → pick
 *  Defective / Incompatible / Missing → continues on the order page with the usual claim form. */
export function NewClaimDialog({ open, onOpenChange, t }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const lookup = useMutation({
    mutationFn: async (n) => (await api.get(`/claims/lookup`, { params: { order_number: n } })).data,
    onSuccess: (d) => setResult(d),
    onError: (e) => { setResult(null); toast.error(e?.response?.data?.detail || t("Lookup failed")); },
  });
  const importShopify = useMutation({
    mutationFn: async (n) => (await api.post(`/claims/import-shopify`, { order_number: n })).data,
    onSuccess: (d) => {
      qc.invalidateQueries();
      if (d.action !== "created") {
        toast.message(d.message || t("Already in the CRM"));
        setResult({ in_crm: true, orders: d.order ? [d.order] : [], shopify: null, order_number: num });
        return;
      }
      toast.success(`${d.order.po_number} ${t("created from Shopify")} · ${d.order.customer_name || d.order.customer_email} · ${t("filed as Fulfilled")}`);
      setResult({ in_crm: true, orders: [d.order], shopify: null, order_number: num, imported: true });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("Shopify import failed")),
  });
  const reset = () => { setNum(""); setResult(null); };
  const start = (order, type) => {
    onOpenChange(false);
    reset();
    navigate(`/order/${order.order_id}?claim=${type}`, { state: { from: "claims" } });
  };
  const go = (e) => { e?.preventDefault?.(); if (num.trim()) lookup.mutate(num.trim()); };
  const sh = result?.shopify;
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-xl" data-testid="new-claim-dialog">
        <DialogHeader>
          <DialogTitle>⚠️ {t("New claim")} <span className="text-slate-400 font-normal text-sm">· {t("by order number")}</span></DialogTitle>
        </DialogHeader>
        <form onSubmit={go} className="flex gap-2">
          <Input autoFocus value={num} onChange={(e) => setNum(e.target.value)} placeholder={t("Order number, e.g. 5057 or PO-5057")} data-testid="new-claim-order-number" />
          <Button type="submit" disabled={!num.trim() || lookup.isPending} data-testid="new-claim-lookup">{lookup.isPending ? t("Searching…") : `🔍 ${t("Find")}`}</Button>
        </form>

        {result && result.in_crm && (
          <div className="space-y-2" data-testid="new-claim-crm-results">
            <div className="text-xs text-slate-500">{result.imported ? t("Imported from Shopify — now pick the claim type:") : t("Found in the CRM — pick the order and the claim type:")}</div>
            {result.orders.map((o) => {
              const fulfilled = o.order_status === "fulfilled";
              return (
                <div key={o.order_id} className="rounded-md border border-slate-200 p-3 text-sm" data-testid={`new-claim-order-${o.po_number}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold">{o.po_number}</span>
                    <Badge variant="outline" className="text-[10px]">{o.order_status}</Badge>
                    {o.cancelled && <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">{t("cancelled")}</Badge>}
                    {o.claim_open && <Badge className="bg-rose-600 text-white text-[10px]">{t("open claim")}</Badge>}
                    {o.claims_count > 0 && !o.claim_open && <span className="text-[10px] text-slate-500">{o.claims_count} {t("past claim(s)")}</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    👤 <b>{o.customer_name || "—"}</b> {o.customer_email ? `· ${o.customer_email}` : ""} {o.customer_phone ? `· ${o.customer_phone}` : ""}
                  </div>
                  <div className="text-xs text-slate-600">
                    🚗 {[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"} · 🎨 {o.color || "—"}{o.logo ? ` · ${o.logo}` : ""} · {t("fulfilled")} {fmtDate(o.fulfilled_at)}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {fulfilled && !o.claim_open ? CLAIM_TYPES.map(([k, l]) => (
                      <Button key={k} size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => start(o, k)} data-testid={`new-claim-start-${k}-${o.po_number}`}>
                        ⚠️ {t(l)}
                      </Button>
                    )) : (
                      <span className="text-xs text-amber-700" data-testid={`new-claim-blocked-${o.po_number}`}>
                        {o.claim_open ? t("This order already has an open claim — decide it first.") : t("Claims can only be filed on Fulfilled orders — current status:")} {o.claim_open ? "" : o.order_status}
                        {" "}<button type="button" className="underline" onClick={() => { onOpenChange(false); reset(); navigate(`/order/${o.order_id}`); }}>{t("Open order")}</button>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {result && !result.in_crm && sh && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm space-y-1" data-testid="new-claim-shopify-preview">
            <div className="text-xs font-semibold text-emerald-900">{t("Not in the CRM yet — found in Shopify:")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-bold">{sh.name || `#${sh.order_number}`}</span>
              <Badge variant="outline" className="text-[10px]">{sh.fulfillment_status}</Badge>
              <Badge variant="outline" className="text-[10px]">{sh.financial_status}</Badge>
              {sh.cancelled_at && <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">{t("cancelled in Shopify")}</Badge>}
            </div>
            <div className="text-xs text-slate-700">👤 <b>{sh.customer_name || "—"}</b> {sh.customer_email ? `· ${sh.customer_email}` : ""} {sh.customer_phone ? `· ${sh.customer_phone}` : ""}</div>
            <div className="text-xs text-slate-700">🚗 {[sh.brand, sh.model, sh.year].filter(Boolean).join(" · ") || "—"} · 🎨 {sh.color || "—"}{sh.logo ? ` · ${sh.logo}` : ""} · 📍 {[sh.ship_city, sh.ship_country].filter(Boolean).join(", ") || "—"}</div>
            <div className="text-xs text-slate-700">🛒 {(sh.items || []).join("; ") || "—"} · {t("ordered")} {fmtDate(sh.created_at)}{sh.fulfilled_at ? ` · ${t("fulfilled")} ${fmtDate(sh.fulfilled_at)}` : ""}</div>
            <div className="text-[11px] text-emerald-900 pt-1">
              {t("Importing creates the customer and the order in the CRM, filed straight as Fulfilled (the customer already has the product). Then you file the claim as usual.")}
            </div>
            <div className="pt-1">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={importShopify.isPending} onClick={() => importShopify.mutate(num.trim())} data-testid="new-claim-import-shopify">
                {importShopify.isPending ? t("Importing…") : `⬇ ${t("Import client & order from Shopify")}`}
              </Button>
            </div>
          </div>
        )}

        {result && !result.in_crm && !sh && (
          <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid="new-claim-not-found">
            {result.message || t("Not found")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
